import { endOfWeek, startOfWeek } from "date-fns"
import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "design-system/molecules/Carousel"
import { Table, TableBody, TableCell, TableRow } from "design-system/molecules/Table"
import { ensure, formatDate, formatPercent } from "lopermut"
import { ArrowLeft, ArrowRight, CornerRightDown, InfoIcon } from "lucide-react"
import { SentimentScore } from "modules/shared/components/Scores/SentimentScore"
import { SolveRateScore } from "modules/shared/components/Scores/SolveRateScore"
import { formatCanonicalTopicLabel } from "modules/shared/lib/formatCanonicalTopicLabel"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CANONICAL_RANKING_TOP_N,
  CanonicalTopicsTimelineResponseDataSchema,
  UNCATEGORIZED_CANONICAL_TOPIC_ID,
} from "schema"
import { z } from "zod"

// Custom hook to handle carousel logic and synchronization
function useCarouselSync(
  slides: Array<{ dateISO: string; snapshots: unknown[] }>,
  effectivePeriodISO: string | undefined,
  onSelectNodeId: (nodeId: string | undefined) => void,
  effectiveTopicId: string,
) {
  const [api, setApi] = useState<CarouselApi | null>(null)

  const currentEffectiveDateRef = useRef<string | undefined>(effectivePeriodISO)
  useEffect(() => {
    currentEffectiveDateRef.current = effectivePeriodISO
  }, [effectivePeriodISO])

  // Avoid emitting during initial mount/re-mount select event
  const ignoreNextSelectRef = useRef<boolean>(true)

  // Scroll to the appropriate slide when effectivePeriodISO or topic changes
  useEffect(() => {
    if (!api || slides.length === 0) return
    // Programmatic scroll disabled to avoid initial flash.
  }, [api, effectivePeriodISO, slides, effectiveTopicId])

  // Handle carousel selection events
  const handleSelect = useCallback(() => {
    if (!api) return

    const selectedIndex = api.selectedScrollSnap()
    const selectedSlide = slides[selectedIndex]

    if (selectedSlide && effectiveTopicId) {
      if (ignoreNextSelectRef.current) {
        ignoreNextSelectRef.current = false
        return
      }
      // Avoid feedback loops: only emit when the newly selected date differs
      if (
        currentEffectiveDateRef.current &&
        selectedSlide.dateISO === currentEffectiveDateRef.current
      ) {
        return
      }
      const nodeId = `${selectedSlide.dateISO}-${effectiveTopicId}`
      onSelectNodeId(nodeId)
    }
  }, [api, slides, effectiveTopicId, onSelectNodeId])

  // Set up carousel API and event listeners
  const handleApiChange = useCallback(
    (carouselApi: CarouselApi | null) => {
      if (!carouselApi) return

      setApi(carouselApi)

      // Set up select event listener
      carouselApi.on("select", handleSelect)
      // Ignore the very first select event right after mount/re-mount
      ignoreNextSelectRef.current = true
    },
    [handleSelect],
  )

  return { api, handleApiChange }
}

type Props = {
  data: z.infer<typeof CanonicalTopicsTimelineResponseDataSchema> | undefined
  isSupport: boolean
  canonicalColorMap: Record<string, string>
  hoveredNodeId?: string
  selectedNodeId?: string
  selectedTopicId?: string
  onSelectTopicId?: (topicId: string | undefined) => void
  onSelectNodeId?: (nodeId: string | undefined) => void
  onApplyFilters?: () => void
  canApplyFilters?: boolean
}

export const CanonicalTopicsTooltip = ({
  data,
  isSupport,
  canonicalColorMap,
  hoveredNodeId,
  selectedNodeId,
  selectedTopicId,
  onSelectTopicId,
  onSelectNodeId,
  onApplyFilters,
  canApplyFilters,
}: Props) => {
  const shouldShowList = !hoveredNodeId && !selectedNodeId && !selectedTopicId

  return (
    <Card className="flex flex-col p-3 shrink-0 w-full lg:w-sm lg:mt-3 lg:-mb-3 shadow-none border border-border border-dashed">
      {shouldShowList && (
        <LastWeekRankingList
          data={data}
          isSupport={isSupport}
          canonicalColorMap={canonicalColorMap}
          onSelectTopicId={onSelectTopicId}
          onApplyFilters={onApplyFilters}
          canApplyFilters={canApplyFilters}
        />
      )}
      {!shouldShowList && (
        <OverallTopicData
          data={data}
          isSupport={isSupport}
          canonicalColorMap={canonicalColorMap}
          hoveredNodeId={hoveredNodeId}
          selectedNodeId={selectedNodeId}
          selectedTopicId={selectedTopicId}
          onSelectNodeId={onSelectNodeId}
          onApplyFilters={onApplyFilters}
          canApplyFilters={canApplyFilters}
        />
      )}
    </Card>
  )
}

const LastWeekRankingList = ({
  data,
  isSupport,
  canonicalColorMap,
  onSelectTopicId,
  onApplyFilters,
  canApplyFilters,
}: Props) => {
  const { t } = useTranslation()

  const latestWeek = data?.timeline?.[data.timeline.length - 1]
  const latestWeekMap = Object.fromEntries(
    (latestWeek?.rankings ?? []).map((r) => [r.canonicalTopicId, r]),
  )

  // Build the list from overallRanking (same source as canonicalColorMap) so that
  // badge colors are always sequential and consistent with the chart line colors.
  const items = Object.entries(data?.overallRanking ?? {})
    .sort((a, b) => (a[1].rank ?? 0) - (b[1].rank ?? 0))
    .slice(0, CANONICAL_RANKING_TOP_N)

  const list = items.map(([id, overallData]) => {
    const weeklyData = latestWeekMap[id]
    const label = formatCanonicalTopicLabel(overallData.label)
    const total = weeklyData?.totalItems ?? overallData.totalItems
    const rank = overallData.rank
    const color = canonicalColorMap[id] ?? "muted-line"
    const avgSentiment = overallData.avgSentiment
    const avgSolveRate = overallData.avgSolveRate
    const isSyntheticUncategorized = id === UNCATEGORIZED_CANONICAL_TOPIC_ID

    return (
      <div
        key={id}
        className={`flex items-center justify-between rounded-md gap-4 ${
          isSyntheticUncategorized ? "" : "cursor-pointer"
        }`}
        onClick={() => {
          if (isSyntheticUncategorized) return

          onSelectTopicId?.(id)
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm p-0"
            style={{ backgroundColor: `var(--${color})` }}
          >
            <span className="text-[10px] leading-none font-semibold text-background">
              {`#${rank}`}
            </span>
          </div>
          <div className="truncate text-sm">{label}</div>
        </div>
        <div className="flex items-center gap-4 min-w-32 justify-between">
          <div className="text-xs text-muted-foreground tabular-nums">
            {isSupport
              ? t("insights.canonicalTimeline.tooltip.totalQuestions", { count: total })
              : t("insights.canonicalTimeline.tooltip.totalInsights", { count: total })}
          </div>
          {!isSupport && <SentimentScore sentiment={avgSentiment} mini />}
          {isSupport && <SolveRateScore solveRate={avgSolveRate} mini />}
        </div>
      </div>
    )
  })

  return (
    <div className="flex flex-col justify-between h-full gap-2 mb-0">
      <div className="flex flex-col gap-2.5">{list}</div>
      <div className="flex items-center justify-between gap-2 min-w-0 pt-2 text-xs">
        <Tooltip>
          <TooltipTrigger>
            <InfoIcon className="size-4 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>{t("insights.canonicalTimeline.tooltip.lastWeekRanking")}</TooltipContent>
        </Tooltip>
        {onApplyFilters && (
          <ApplyFiltersButton onApplyFilters={onApplyFilters} canApplyFilters={canApplyFilters} />
        )}
      </div>
    </div>
  )
}

const OverallTopicData = ({
  data,
  isSupport,
  canonicalColorMap,
  hoveredNodeId,
  selectedNodeId,
  selectedTopicId,
  onSelectNodeId,
  canApplyFilters,
  onApplyFilters,
}: Props) => {
  const { t } = useTranslation()

  // Derive effective topicId and periodISO from selection/hover
  const { effectiveTopicId, effectivePeriodISO } = useMemo(() => {
    const parseNodeId = (nodeId: string | undefined) => {
      if (!nodeId) return { dateISO: undefined, topicId: undefined }

      const lastDash = nodeId.lastIndexOf("-")
      if (lastDash <= 0 || lastDash >= nodeId.length - 1) {
        return { dateISO: undefined, topicId: undefined }
      }

      return {
        dateISO: nodeId.slice(0, lastDash),
        topicId: nodeId.slice(lastDash + 1),
      }
    }

    if (selectedNodeId) {
      const parts = parseNodeId(selectedNodeId)
      return {
        // Derive topic directly from selected node to avoid transient mismatch
        effectiveTopicId: parts.topicId ?? selectedTopicId,
        effectivePeriodISO: parts.dateISO,
      }
    }
    if (hoveredNodeId) {
      const parts = parseNodeId(hoveredNodeId)
      return {
        effectiveTopicId: parts.topicId ?? selectedTopicId,
        effectivePeriodISO: parts.dateISO,
      }
    }
    return { effectiveTopicId: selectedTopicId, effectivePeriodISO: undefined }
  }, [hoveredNodeId, selectedNodeId, selectedTopicId])

  // Memoize slides calculation - must be before any early returns
  const slides = useMemo(() => {
    if (!data || !effectiveTopicId) return []

    // Ensure we have timeline data for this topic
    const hasDataForTopic = data.timeline?.some((period) =>
      period.rankings.some((r) => r.canonicalTopicId === effectiveTopicId),
    )

    if (!hasDataForTopic) return []

    return (data.timeline ?? [])
      .map((period) => {
        const ranking = period.rankings.find((r) => r.canonicalTopicId === effectiveTopicId)
        if (!ranking) return null
        return {
          dateISO: period.date,
          snapshots: ranking.snapshots ?? [],
        }
      })
      .filter(Boolean)
    // Keep weeks even if there are zero snapshots so the carousel can
    // align to the exact selected week from the chart
  }, [data, effectiveTopicId])

  const groupedSlides = useMemo(() => {
    const acc: Record<
      string,
      {
        label: string
        totalItems: number
        snapshots: Array<(typeof slides)[0]["snapshots"][0]>
        avgSentiment: number
        avgSolveRate: number
      }
    > = {}

    slides.forEach((slide) => {
      slide.snapshots.forEach((s) => {
        if (!s.label) return

        if (!acc[s.label]) {
          acc[s.label] = {
            label: s.label,
            totalItems: 0,
            snapshots: [],
            avgSentiment: 0,
            avgSolveRate: 0,
          }
        }

        ensure(acc[s.label]).totalItems += s.itemsCount ?? 0
        ensure(acc[s.label]).avgSentiment += s.overallSentiment ?? 0
        ensure(acc[s.label]).avgSolveRate += s.overallSolveRate ?? 0
        ensure(acc[s.label]).snapshots.push(s)

        return acc
      })
    })

    return Object.values(acc)
  }, [slides])

  // Use custom hook for carousel logic - must be before any early returns
  const { api, handleApiChange } = useCarouselSync(
    slides,
    effectivePeriodISO,
    // Only propagate node changes if the selected node actually differs, to avoid loops
    (nodeId) => {
      if (!onSelectNodeId) return
      const next = nodeId
      const current = selectedNodeId
      if (next === current) return
      onSelectNodeId(next)
    },
    effectiveTopicId || "",
  )

  // Custom navigation functions
  const scrollPrev = useCallback(() => {
    api?.scrollPrev(false)
  }, [api])

  const scrollNext = useCallback(() => {
    api?.scrollNext(false)
  }, [api])

  const canScrollPrev = api?.canScrollPrev() ?? false
  const canScrollNext = api?.canScrollNext() ?? false

  // Compute the initial slide index for Embla to avoid any initial flash
  const initialSlideIndex = useMemo(() => {
    if (!slides.length) return 0
    if (effectivePeriodISO) {
      const idx = slides.findIndex((s) => s.dateISO === effectivePeriodISO)
      if (idx >= 0) return idx
    }
    return slides.length - 1
  }, [slides, effectivePeriodISO])

  // Early return after all hooks
  if (!effectiveTopicId) return null

  // Try to get data from timeline first, then fallback to canonical topics
  const overall = data?.overallRanking?.[effectiveTopicId]
  if (!overall) return null

  // Use timeline data if available, otherwise use fallback data
  const rank = overall?.rank ?? null
  const label = formatCanonicalTopicLabel(overall?.label) ?? ""
  const description = overall?.description ?? ""
  const share = overall?.share ?? null
  const avgSentiment = overall?.avgSentiment ?? null
  const avgSolveRate = overall?.avgSolveRate ?? null

  const color = canonicalColorMap[effectiveTopicId] ?? "muted-line"

  return (
    <div className="flex flex-col justify-between items-end h-full gap-2">
      <div className="flex flex-col gap-4 w-full lg:max-w-sm">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              {rank !== null && (
                <div
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm p-0"
                  style={{ backgroundColor: `var(--${color})` }}
                >
                  <span className="text-[10px] leading-none font-semibold text-background">
                    {`#${rank}`}
                  </span>
                </div>
              )}
              <div className="text-sm line-clamp-2 break-words min-w-0">{label}</div>
            </div>
            <div className="flex flex-row items-center gap-4 shrink-0">
              {share !== null && (
                <Tooltip>
                  <TooltipTrigger slick>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      {t("insights.canonicalTimeline.tooltip.totalShare", {
                        value: formatPercent({ amount: share, fix: 0 }, true),
                      })}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("insights.canonicalTimeline.tooltip.shareTooltip")}
                  </TooltipContent>
                </Tooltip>
              )}
              {!isSupport && avgSentiment !== null && (
                <SentimentScore sentiment={avgSentiment} mini />
              )}
              {isSupport && avgSolveRate !== null && (
                <SolveRateScore solveRate={avgSolveRate} mini />
              )}
            </div>
          </div>
          {description && (
            <div
              className="text-xs text-muted-foreground leading-snug line-clamp-2"
              title={description}
            >
              {description}
            </div>
          )}
        </div>
        {groupedSlides.length > 0 && !effectivePeriodISO && (
          <div className="flex flex-col">
            <Table>
              <TableBody>
                {groupedSlides.slice(0, 4).map((s) => (
                  <TableRow key={s.label} className="h-6">
                    <TableCell className="py-0 pl-0 h-8 text-xs truncate max-w-32">
                      {s.label}
                    </TableCell>
                    <TableCell className="py-0 pr-0 h-8 text-xs" align="right">
                      <div className="flex flex-row items-center justify-end gap-4 shrink-0">
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {t("insights.canonicalTimeline.tooltip.totalConversations", {
                            count: s.totalItems ?? 0,
                          })}
                        </span>
                        {!isSupport && <SentimentScore sentiment={s.avgSentiment} mini />}
                        {isSupport && <SolveRateScore solveRate={s.avgSolveRate} mini />}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {slides.length > 0 && effectivePeriodISO && (
          <Carousel
            className="w-full [&>*]:transition-none"
            setApi={handleApiChange}
            opts={{
              align: "start",
              duration: 0, // Disable slide animations
              dragFree: false, // Disable free dragging
              startIndex: initialSlideIndex,
            }}
            key={`${effectiveTopicId}-${initialSlideIndex}`} // Force re-mount when topic or week changes
          >
            <CarouselContent>
              {slides.map((slide, idx) => {
                const dateLabel = (() => {
                  try {
                    const weekStart = startOfWeek(slide.dateISO, { weekStartsOn: 1 })
                    const weekEnd = endOfWeek(slide.dateISO, { weekStartsOn: 1 })
                    return `${formatDate(weekStart, "P")} - ${formatDate(weekEnd, "P")}`
                  } catch (_e) {
                    return slide.dateISO
                  }
                })()
                return (
                  <CarouselItem key={`${slide.dateISO}-${idx}`} className="basis-full">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <Button
                          variant="outline"
                          size="icon"
                          className="size-6 border-none shadow-none hover:shadow-sm hover:bg-muted"
                          onClick={scrollPrev}
                          disabled={!canScrollPrev}
                        >
                          <ArrowLeft className="size-3" />
                        </Button>
                        <div className="text-xs font-medium">{dateLabel}</div>
                        <Button
                          variant="outline"
                          size="icon"
                          className="size-6 border-none shadow-none hover:shadow-sm hover:bg-muted"
                          onClick={scrollNext}
                          disabled={!canScrollNext}
                        >
                          <ArrowRight className="size-3" />
                        </Button>
                      </div>
                      <div className="max-h-32 overflow-y-auto">
                        <Table>
                          <TableBody>
                            {slide.snapshots
                              .sort((a, b) => (b.itemsCount ?? 0) - (a.itemsCount ?? 0))
                              ?.map((s) => (
                                <TableRow key={s.id} className="h-6">
                                  <TableCell className="py-0 pl-0 h-8 text-xs truncate max-w-32">
                                    {s.label}
                                  </TableCell>
                                  <TableCell className="py-0 pr-0 h-8 text-xs" align="right">
                                    <div className="flex flex-row items-center justify-end gap-4 shrink-0">
                                      <span className="text-xs text-muted-foreground tabular-nums">
                                        {t(
                                          "insights.canonicalTimeline.tooltip.totalConversations",
                                          {
                                            count: s.conversationsCount ?? 0,
                                          },
                                        )}
                                      </span>
                                      {!isSupport && (
                                        <SentimentScore sentiment={s.overallSentiment} mini />
                                      )}
                                      {isSupport && (
                                        <SolveRateScore solveRate={s.overallSolveRate} mini />
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CarouselItem>
                )
              })}
            </CarouselContent>
          </Carousel>
        )}
      </div>
      {onApplyFilters && (
        <ApplyFiltersButton onApplyFilters={onApplyFilters} canApplyFilters={canApplyFilters} />
      )}
    </div>
  )
}

function ApplyFiltersButton({
  onApplyFilters,
  canApplyFilters,
}: Pick<Props, "onApplyFilters" | "canApplyFilters">) {
  const { t } = useTranslation()

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onApplyFilters}
      className="w-fit"
      disabled={!canApplyFilters}
    >
      <CornerRightDown />
      <span>{t("shared.applyFilters")}</span>
    </Button>
  )
}
